import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  Divider,
  Typography,
  Table,
  message,
  Switch,
  Modal,
  Alert,
  Checkbox,
  Tooltip,
} from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  createDeliveryChallanRequest,
} from '../../redux/actions/deliveryChallanActions';
import { fetchOrdersRequest } from '../../containers/PurchaseOrder/actions';
import { hasPermission, PERMISSIONS } from '../../utils/permissions';

const { Option } = Select;
const { TextArea } = Input;

const toSafeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundMoney = (value) => Math.round((toSafeNumber(value) + Number.EPSILON) * 100) / 100;

const formatCurrency = (value) =>
  `₹${toSafeNumber(value).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getFirstNumericValue = (source, keys, fallback = 0) => {
  for (const key of keys) {
    const rawValue = source?.[key];
    if (rawValue === undefined || rawValue === null || rawValue === '') {
      continue;
    }

    const parsed = Number(rawValue);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return fallback;
};

const computeItemFinancials = (item, selectedQuantity) => {
  const quantity = Math.max(0, toSafeNumber(selectedQuantity));
  const orderedQuantity = Math.max(
    0,
    getFirstNumericValue(item, ['ordered_quantity', 'quantity'])
  );
  const rate = Math.max(
    0,
    getFirstNumericValue(item, ['rate', 'purchase_rate', 'unit_rate', 'billed_rate'])
  );

  const discountPercentage = Math.max(
    0,
    getFirstNumericValue(item, ['discount', 'discount_percentage', 'discount_percent'])
  );
  const taxPercentage = Math.max(
    0,
    getFirstNumericValue(item, ['tax_slab', 'tax_percentage', 'gst_percentage'])
  );

  const subtotalAmount = quantity * rate;

  const discountAmountFromLine =
    orderedQuantity > 0
      ? (Math.max(0, getFirstNumericValue(item, ['discount_amount'])) / orderedQuantity) * quantity
      : 0;

  const discountAmount =
    discountPercentage > 0
      ? (subtotalAmount * discountPercentage) / 100
      : discountAmountFromLine;

  const taxableAmount = Math.max(0, subtotalAmount - discountAmount);

  const taxAmountFromLine =
    orderedQuantity > 0
      ? (Math.max(0, getFirstNumericValue(item, ['tax_amount'])) / orderedQuantity) * quantity
      : 0;

  const taxAmount =
    taxPercentage > 0 ? (taxableAmount * taxPercentage) / 100 : taxAmountFromLine;

  const finalAmount = taxableAmount + taxAmount;

  return {
    quantity,
    rate: roundMoney(rate),
    discount_percentage: roundMoney(discountPercentage),
    tax_percentage: roundMoney(taxPercentage),
    subtotal_amount: roundMoney(subtotalAmount),
    discount_amount: roundMoney(discountAmount),
    taxable_amount: roundMoney(taxableAmount),
    tax_amount: roundMoney(taxAmount),
    final_amount: roundMoney(finalAmount),
  };
};

const DeliveryChallanForm = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const permissions = useSelector((state) => state.auth.permissions || []);
  const canAmendQuantity = hasPermission(permissions, PERMISSIONS.PURCHASE_ORDER_AMEND);
  // Holds the pending confirmation prompt shown when a user tries to record
  // more than the PO's pending quantity for one or more items.
  const [amendModal, setAmendModal] = useState({ open: false, items: [], reason: '' });
  // Reason applied to every item flagged with close_shortfall when submitting.
  const [shortfallReason, setShortfallReason] = useState('');

  const { orders } = useSelector((state) => state.orders);
  const { loading } = useSelector((state) => state.deliveryChallans);

  const [selectedPO, setSelectedPO] = useState(null);
  const [itemsData, setItemsData] = useState([]);
  const [isPartialDelivery, setIsPartialDelivery] = useState(false);

  const normalizeStatus = (status) => String(status || '').trim().toLowerCase();

  const hasPendingDelivery = (order) => {
    if (!order) return false;

    if (Array.isArray(order.items) && order.items.length > 0) {
      return order.items.some((item) => {
        const orderedQuantity = Number(item.quantity ?? item.ordered_quantity ?? 0);
        const deliveredQuantity = Number(item.delivered_quantity ?? 0);
        const pendingQuantity = Math.max(0, orderedQuantity - deliveredQuantity);
        return pendingQuantity > 0;
      });
    }

    const deliveryStatus = normalizeStatus(order.delivery_status);
    if (deliveryStatus) {
      return !['delivered', 'cancelled'].includes(deliveryStatus);
    }

    const orderStatus = normalizeStatus(order.order_status);
    if (orderStatus) {
      return !['delivered', 'closed', 'cancelled', 'verified', 'billed'].includes(orderStatus);
    }

    return false;
  };

  const getOrderPendingQuantity = (order) => {
    if (!order || !Array.isArray(order.items)) return 0;

    return order.items.reduce((sum, item) => {
      const orderedQuantity = Number(item.quantity ?? item.ordered_quantity ?? 0);
      const deliveredQuantity = Number(item.delivered_quantity ?? 0);
      return sum + Math.max(0, orderedQuantity - deliveredQuantity);
    }, 0);
  };

  const getOrderUrgencyTimestamp = (order) => {
    const urgencyDate =
      order?.delivery_date ||
      order?.deliveryDate ||
      order?.created_at ||
      order?.createdAt;

    if (!urgencyDate) {
      return Number.POSITIVE_INFINITY;
    }

    const parsed = dayjs(urgencyDate);
    return parsed.isValid() ? parsed.valueOf() : Number.POSITIVE_INFINITY;
  };

  const availableOrders = useMemo(
    () =>
      (orders || [])
        .filter((order) => hasPendingDelivery(order))
        .map((order) => ({
          ...order,
          pending_delivery_quantity: getOrderPendingQuantity(order),
          urgency_timestamp: getOrderUrgencyTimestamp(order),
        }))
        .sort((a, b) => {
          const pendingDiff =
            (b.pending_delivery_quantity || 0) - (a.pending_delivery_quantity || 0);
          if (pendingDiff !== 0) return pendingDiff;

          const urgencyDiff =
            (a.urgency_timestamp || Number.POSITIVE_INFINITY) -
            (b.urgency_timestamp || Number.POSITIVE_INFINITY);
          if (urgencyDiff !== 0) return urgencyDiff;

          return Number(a.id || 0) - Number(b.id || 0);
        }),
    [orders]
  );

  const getItemQuantitiesByMode = (pendingQuantity, partialDelivery) => {
    const deliveringQuantity = partialDelivery ? 0 : pendingQuantity;
    return {
      delivering_quantity: deliveringQuantity,
      rejected_quantity: 0,
      accepted_quantity: deliveringQuantity,
    };
  };

  useEffect(() => {
    dispatch(fetchOrdersRequest(1, 100));
  }, [dispatch]);

  useEffect(() => {
    if (!selectedPO) return;

    const isSelectedPOAvailable = availableOrders.some(
      (order) => order.id === selectedPO.id
    );

    if (!isSelectedPOAvailable) {
      setSelectedPO(null);
      setItemsData([]);
      form.setFieldsValue({ purchase_order_id: undefined });
    }
  }, [availableOrders, selectedPO, form]);

  const handlePOChange = (poId) => {
    const po = availableOrders.find((o) => o.id === poId);
    if (po && po.items) {
      setSelectedPO(po);
      const items = po.items.map((item) => {
        const pendingQuantity = Math.max(
          0,
          (item.quantity || 0) - (item.delivered_quantity || 0)
        );

        return {
          key: item.id,
          purchase_order_item_id: item.id,
          product_id: item.product_id,
          product_name: item.product_name,
          ordered_quantity: item.quantity,
          unit: item.unit || '',
          delivered_quantity_so_far: item.delivered_quantity || 0,
          pending_quantity: pendingQuantity,
          rate: getFirstNumericValue(item, ['rate', 'purchase_rate', 'unit_rate', 'billed_rate']),
          discount: getFirstNumericValue(item, ['discount', 'discount_percentage', 'discount_percent']),
          discount_amount: getFirstNumericValue(item, ['discount_amount']),
          tax_slab: getFirstNumericValue(item, ['tax_slab', 'tax_percentage', 'gst_percentage']),
          tax_amount: getFirstNumericValue(item, ['tax_amount']),
          ...getItemQuantitiesByMode(pendingQuantity, isPartialDelivery),
          item_status: 'received',
          quality_remarks: '',
        };
      });
      setItemsData(items);
    } else {
      setSelectedPO(null);
      setItemsData([]);
    }
  };

  const handlePartialDeliveryChange = (checked) => {
    setIsPartialDelivery(checked);

    if (checked) {
      return;
    }

    setItemsData((prev) =>
      prev.map((item) => ({
        ...item,
        ...getItemQuantitiesByMode(item.pending_quantity || 0, false),
      }))
    );
  };

  const handleItemChange = (key, field, value) => {
    setItemsData((prev) =>
      prev.map((item) => {
        if (item.key !== key) return item;

        if (
          !isPartialDelivery &&
          (field === 'delivering_quantity' || field === 'rejected_quantity')
        ) {
          return item;
        }

        const updated = { ...item, [field]: value };

        if (field === 'delivering_quantity') {
          // Note: no longer clamped to pending_quantity here — a value above
          // pending is allowed (vendor over-supplied) and is caught at submit
          // time, where the user is asked to confirm a PO quantity amendment.
          const safeDelivering = Math.max(0, value || 0);
          const safeRejected = Math.min(item.rejected_quantity || 0, safeDelivering);

          updated.delivering_quantity = safeDelivering;
          updated.rejected_quantity = safeRejected;
          updated.accepted_quantity = Math.max(0, safeDelivering - safeRejected);
        }

        if (field === 'rejected_quantity') {
          const delivering = item.delivering_quantity || 0;
          const safeRejected = Math.max(0, Math.min(value || 0, delivering));

          updated.rejected_quantity = safeRejected;
          updated.accepted_quantity = Math.max(0, delivering - safeRejected);
        }

        return updated;
      })
    );
  };

  const deliveryAmountSummary = useMemo(() => {
    const totals = itemsData.reduce(
      (acc, item) => {
        const deliveredQuantity = toSafeNumber(item.delivering_quantity);
        if (deliveredQuantity <= 0) {
          return acc;
        }

        const acceptedQuantity = Math.max(
          0,
          toSafeNumber(item.accepted_quantity ?? deliveredQuantity)
        );

        const computed = computeItemFinancials(item, acceptedQuantity);

        return {
          total_items: acc.total_items + 1,
          delivered_quantity: acc.delivered_quantity + deliveredQuantity,
          accepted_quantity: acc.accepted_quantity + acceptedQuantity,
          subtotal_amount: acc.subtotal_amount + computed.subtotal_amount,
          discount_amount: acc.discount_amount + computed.discount_amount,
          taxable_amount: acc.taxable_amount + computed.taxable_amount,
          tax_amount: acc.tax_amount + computed.tax_amount,
          final_amount: acc.final_amount + computed.final_amount,
        };
      },
      {
        total_items: 0,
        delivered_quantity: 0,
        accepted_quantity: 0,
        subtotal_amount: 0,
        discount_amount: 0,
        taxable_amount: 0,
        tax_amount: 0,
        final_amount: 0,
      }
    );

    return {
      total_items: totals.total_items,
      delivered_quantity: roundMoney(totals.delivered_quantity),
      accepted_quantity: roundMoney(totals.accepted_quantity),
      subtotal_amount: roundMoney(totals.subtotal_amount),
      discount_amount: roundMoney(totals.discount_amount),
      taxable_amount: roundMoney(totals.taxable_amount),
      tax_amount: roundMoney(totals.tax_amount),
      final_amount: roundMoney(totals.final_amount),
    };
  }, [itemsData]);

  // ── Form submit ──────────────────────────────────────────────────
  // Builds the challan payload and dispatches it. `amendment` (when given)
  // carries { reason } and marks that the user has confirmed amending the PO
  // quantity for any item(s) that exceed their pending quantity.
  const submitChallan = (values, itemsWithDelivery, amendment) => {
    const shortfallItems = itemsWithDelivery.filter((item) => item.close_shortfall);
    const payload = {
      purchase_order_id: values.purchase_order_id,
      challan_number: values.challan_number,
      challan_date: values.challan_date
        ? values.challan_date.format('YYYY-MM-DD')
        : null,
      delivery_date: values.delivery_date
        ? values.delivery_date.format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD'),
      is_partial_delivery: isPartialDelivery,
      subtotal_amount: deliveryAmountSummary.subtotal_amount,
      discount_amount: deliveryAmountSummary.discount_amount,
      taxable_amount: deliveryAmountSummary.taxable_amount,
      tax_amount: deliveryAmountSummary.tax_amount,
      final_amount: deliveryAmountSummary.final_amount,
      total_amount: deliveryAmountSummary.final_amount,
      remarks: values.remarks || '',
      attachment_url: values.attachment_url || '',
      items: itemsWithDelivery.map((item) => ({
        purchase_order_item_id: item.purchase_order_item_id,
        product_id: item.product_id,
        product_name: item.product_name,
        ordered_quantity: item.ordered_quantity,
        delivered_quantity: item.delivering_quantity,
        accepted_quantity: item.accepted_quantity,
        rejected_quantity: item.rejected_quantity || 0,
        item_status: item.item_status || 'received',
        quality_remarks: item.quality_remarks || '',
      })),
      ...(amendment
        ? { confirm_quantity_amendment: true, amendment_reason: amendment.reason }
        : {}),
      ...(shortfallItems.length > 0
        ? {
            close_shortfall_items: shortfallItems.map((item) => ({
              purchase_order_item_id: item.purchase_order_item_id,
            })),
            close_shortfall_reason: shortfallReason.trim(),
          }
        : {}),
    };

    dispatch(createDeliveryChallanRequest(payload));
    // Saga handles list/statistics refresh, and any close_shortfall_items, after success
    form.resetFields();
    setItemsData([]);
    setSelectedPO(null);
    setAmendModal({ open: false, items: [], reason: '' });
    setShortfallReason('');
    onSuccess();
  };

  const handleSubmit = async (values) => {
    const itemsWithDelivery = itemsData.filter(
      (item) => (item.delivering_quantity || 0) > 0
    );

    if (itemsWithDelivery.length === 0) {
      message.error('Please enter delivery quantity for at least one item');
      return;
    }

    const shortfallItems = itemsWithDelivery.filter((item) => item.close_shortfall);
    if (shortfallItems.length > 0 && !shortfallReason.trim()) {
      message.error('Please enter a reason for closing the short-delivered line(s)');
      return;
    }

    const overDelivered = itemsWithDelivery.filter(
      (item) => item.delivering_quantity > item.pending_quantity
    );

    if (overDelivered.length > 0) {
      if (!canAmendQuantity) {
        message.error(
          `Delivery quantity for "${overDelivered[0].product_name}" exceeds the pending quantity ` +
            `(${overDelivered[0].pending_quantity}). You don't have permission to amend the purchase ` +
            `order's quantity — ask an admin to record the extra quantity.`
        );
        return;
      }
      // Ask for confirmation + a reason before amending the PO and submitting.
      setAmendModal({ open: true, items: overDelivered, reason: '', values, itemsWithDelivery });
      return;
    }

    submitChallan(values, itemsWithDelivery);
  };

  const handleConfirmAmendment = () => {
    if (!amendModal.reason || !amendModal.reason.trim()) {
      message.error('Please enter a reason for the quantity amendment');
      return;
    }
    submitChallan(amendModal.values, amendModal.itemsWithDelivery, { reason: amendModal.reason.trim() });
  };

  // ── Item columns for detailed table ──────────────────────────────────────
  const itemColumns = [
    {
      title: 'Product',
      dataIndex: 'product_name',
      key: 'product_name',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Ordered',
      dataIndex: 'ordered_quantity',
      key: 'ordered_quantity',
      width: 95,
      align: 'center',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <strong>{v}</strong>,
    },
    {
      title: 'Delivered',
      dataIndex: 'delivered_quantity_so_far',
      key: 'delivered_quantity_so_far',
      width: 100,
      align: 'center',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <span style={{ color: '#52c41a' }}>{v}</span>,
    },
    {
      title: 'Pending',
      dataIndex: 'pending_quantity',
      key: 'pending_quantity',
      width: 95,
      align: 'center',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <span style={{ color: '#fa8c16', fontWeight: 600 }}>{v}</span>,
    },
    {
      title: 'Delivering Now *',
      key: 'delivering_quantity',
      width: 190,
      render: (_, record) => {
        const exceedsPending = (record.delivering_quantity || 0) > (record.pending_quantity || 0);
        const shortfall = Math.max(0, (record.pending_quantity || 0) - (record.delivering_quantity || 0));
        const isShort = (record.delivering_quantity || 0) > 0 && shortfall > 0.0005;
        return (
          <div>
            <InputNumber
              min={0}
              // No `max` here on purpose — a value above pending_quantity is
              // allowed (vendor over-supplied). It's flagged below and, on
              // submit, prompts for a purchase-order quantity amendment.
              value={record.delivering_quantity}
              onChange={(val) => handleItemChange(record.key, 'delivering_quantity', val)}
              style={{ width: '100%', borderColor: exceedsPending ? '#faad14' : undefined }}
              placeholder={isPartialDelivery ? 'Qty' : 'Auto'}
              disabled={!isPartialDelivery}
            />
            {exceedsPending && (
              <div style={{ fontSize: 11, color: '#d46b08', marginTop: 2 }}>
                <WarningOutlined /> exceeds pending by{' '}
                {(record.delivering_quantity - record.pending_quantity).toFixed(3).replace(/\.?0+$/, '')}
              </div>
            )}
            {isShort && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 11, color: '#fa8c16' }}>
                  {shortfall.toFixed(3).replace(/\.?0+$/, '')} short of pending
                </div>
                {canAmendQuantity ? (
                  <Checkbox
                    checked={!!record.close_shortfall}
                    onChange={(e) => handleItemChange(record.key, 'close_shortfall', e.target.checked)}
                  >
                    <span style={{ fontSize: 11 }}>No more expected — close line</span>
                  </Checkbox>
                ) : (
                  <Tooltip title="You do not have permission to amend purchase order quantities.">
                    <Checkbox disabled>
                      <span style={{ fontSize: 11 }}>No more expected — close line</span>
                    </Checkbox>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Rejected',
      key: 'rejected_quantity',
      width: 100,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (_, record) => (
        <InputNumber
          min={0}
          max={record.delivering_quantity || 0}
          value={record.rejected_quantity}
          onChange={(val) => handleItemChange(record.key, 'rejected_quantity', val)}
          style={{ width: '100%' }}
          placeholder={isPartialDelivery ? '0' : 'Auto'}
          disabled={!isPartialDelivery}
        />
      ),
    },
    {
      title: 'Accepted',
      dataIndex: 'accepted_quantity',
      key: 'accepted_quantity',
      width: 95,
      align: 'center',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (v) => <strong style={{ color: '#1890ff' }}>{v}</strong>,
    },
    {
      title: 'Line Amount (Accepted)',
      key: 'line_amount',
      width: 130,
      align: 'right',
      render: (_, record) => {
        const computed = computeItemFinancials(
          record,
          Math.max(0, toSafeNumber(record.accepted_quantity ?? record.delivering_quantity))
        );
        return <strong style={{ color: '#531dab' }}>{formatCurrency(computed.final_amount)}</strong>;
      },
    },
    {
      title: 'Status',
      key: 'item_status',
      width: 130,
      render: (_, record) => (
        <Select
          value={record.item_status}
          onChange={(val) => handleItemChange(record.key, 'item_status', val)}
          style={{ width: '100%' }}
          size="small"
        >
          <Option value="received">Received</Option>
          <Option value="accepted">Accepted</Option>
          <Option value="rejected">Rejected</Option>
          <Option value="damaged">Damaged</Option>
        </Select>
      ),
    },
    {
      title: 'Quality Remarks',
      key: 'quality_remarks',
      width: 180,
      render: (_, record) => (
        <Input
          value={record.quality_remarks}
          onChange={(e) =>
            handleItemChange(record.key, 'quality_remarks', e.target.value)
          }
          placeholder="Optional"
        />
      ),
    },
  ];

  return (
    <>
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        delivery_date: dayjs(),
        challan_date: dayjs(),
      }}
    >
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Record item-level quantities including accepted/rejected counts, quality remarks, and computed delivery amount.
      </Typography.Text>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Purchase Order (Pending Delivery)"
            name="purchase_order_id"
            rules={[{ required: true, message: 'Select a purchase order' }]}
            extra={
              availableOrders.length > 0
                ? `${availableOrders.length} purchase order(s) available (sorted by highest pending quantity, then oldest delivery date)`
                : 'No purchase orders with pending delivery are available'
            }
          >
            <Select
              placeholder="Select Purchase Order with pending delivery"
              showSearch
              optionFilterProp="children"
              onChange={handlePOChange}
              notFoundContent="No purchase orders pending delivery"
            >
              {availableOrders.map((order) => (
                <Option key={order.id} value={order.id}>
                  PO #{order.id} — {order.vendor_name} (Pending: {order.pending_delivery_quantity || 0})
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item
            label="Challan Number"
            name="challan_number"
            rules={[{ required: true, message: 'Enter challan number' }]}
          >
            <Input placeholder="e.g. CH-2024-001" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Form.Item label="Challan Date" name="challan_date">
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={8}>
          <Form.Item
            label="Delivery Date"
            name="delivery_date"
            rules={[{ required: true, message: 'Select delivery date' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={8}>
          <Form.Item label="Partial Delivery">
            <Space>
              <Switch
                checked={isPartialDelivery}
                onChange={handlePartialDeliveryChange}
                checkedChildren="Yes"
                unCheckedChildren="No"
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {isPartialDelivery
                  ? 'Edit item quantities manually (partial delivery)'
                  : 'Auto-fill mode: Delivering Now = Pending, Rejected = 0'}
              </Typography.Text>
            </Space>
          </Form.Item>
        </Col>
      </Row>

      {selectedPO && (
        <>
          <Divider orientation="left">
            <Typography.Text strong>Delivery Items</Typography.Text>
          </Divider>
          <Typography.Text
            type="secondary"
            style={{ display: 'block', marginBottom: 8, fontSize: 12 }}
          >
            {isPartialDelivery
              ? 'You can edit Delivering Now and Rejected quantities for each line item. Final amount is auto-calculated from quantity, discount, and tax.'
              : 'For full delivery, quantities are auto-filled and locked to avoid accidental edits. Final amount is auto-calculated from quantity, discount, and tax.'}
          </Typography.Text>
          <Table
            columns={itemColumns}
            dataSource={itemsData}
            pagination={false}
            size="small"
            scroll={{ x: 1350 }}
            bordered
          />

          {itemsData.some((item) => item.close_shortfall) && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message="Reason for closing short line(s)"
              description={
                <TextArea
                  rows={2}
                  placeholder="e.g. vendor confirmed no further delivery is coming…"
                  value={shortfallReason}
                  onChange={(e) => setShortfallReason(e.target.value)}
                />
              }
            />
          )}

          <div
            style={{
              marginTop: 12,
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              background: '#fafafa',
              padding: 12,
            }}
          >
            <Row gutter={[16, 8]} align="middle">
              <Col xs={12} sm={8} md={4}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Delivered Qty
                </Typography.Text>
                <div style={{ fontWeight: 600 }}>{deliveryAmountSummary.delivered_quantity}</div>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Accepted Qty
                </Typography.Text>
                <div style={{ fontWeight: 600 }}>{deliveryAmountSummary.accepted_quantity}</div>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Subtotal
                </Typography.Text>
                <div style={{ fontWeight: 600 }}>{formatCurrency(deliveryAmountSummary.subtotal_amount)}</div>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Discount
                </Typography.Text>
                <div style={{ fontWeight: 600, color: '#cf1322' }}>
                  -{formatCurrency(deliveryAmountSummary.discount_amount)}
                </div>
              </Col>
              <Col xs={12} sm={8} md={3}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Taxable
                </Typography.Text>
                <div style={{ fontWeight: 600 }}>{formatCurrency(deliveryAmountSummary.taxable_amount)}</div>
              </Col>
              <Col xs={12} sm={8} md={3}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Tax
                </Typography.Text>
                <div style={{ fontWeight: 600, color: '#fa8c16' }}>
                  +{formatCurrency(deliveryAmountSummary.tax_amount)}
                </div>
              </Col>
              <Col xs={24} sm={8} md={3}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Final Amount
                </Typography.Text>
                <div style={{ fontWeight: 700, color: '#1677ff', fontSize: 18 }}>
                  {formatCurrency(deliveryAmountSummary.final_amount)}
                </div>
              </Col>
            </Row>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              This final amount is included in the Create Delivery Challan API payload.
            </Typography.Text>
          </div>
        </>
      )}

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col xs={24} sm={16}>
          <Form.Item label="Remarks" name="remarks">
            <TextArea rows={2} placeholder="Any additional remarks or notes" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item label="Attachment URL" name="attachment_url">
            <Input placeholder="https://example.com/challan.pdf" />
          </Form.Item>
        </Col>
      </Row>

      <Divider />
      <Form.Item style={{ marginBottom: 0 }}>
        <Space style={{ float: 'right' }}>
          <Button icon={<CloseOutlined />} onClick={onSuccess}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={loading}
          >
            Create Delivery Challan
          </Button>
        </Space>
      </Form.Item>
    </Form>

    <Modal
      title={
        <span>
          <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
          Confirm Purchase Order Quantity Amendment
        </span>
      }
      open={amendModal.open}
      onOk={handleConfirmAmendment}
      onCancel={() => setAmendModal({ open: false, items: [], reason: '' })}
      okText="Amend PO & Record Delivery"
      cancelText="Cancel"
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="The vendor delivered more than what was ordered"
        description="Confirming will increase the ordered quantity on the purchase order for the item(s) below, so the bill can be raised for what was actually received."
      />
      <Table
        dataSource={amendModal.items}
        rowKey="key"
        pagination={false}
        size="small"
        columns={[
          { title: 'Product', dataIndex: 'product_name', key: 'product_name' },
          {
            title: 'Ordered',
            key: 'ordered',
            render: (_, r) => `${r.pending_quantity + r.delivered_quantity_so_far} ${r.unit || ''}`,
          },
          { title: 'Pending', dataIndex: 'pending_quantity', key: 'pending_quantity' },
          { title: 'Receiving', dataIndex: 'delivering_quantity', key: 'delivering_quantity' },
          {
            title: 'Excess',
            key: 'excess',
            render: (_, r) => (
              <span style={{ color: '#d46b08', fontWeight: 600 }}>
                +{(r.delivering_quantity - r.pending_quantity).toFixed(3).replace(/\.?0+$/, '')}
              </span>
            ),
          },
        ]}
        style={{ marginBottom: 16 }}
      />
      <Form.Item
        label="Reason for amendment"
        required
        style={{ marginBottom: 0 }}
      >
        <TextArea
          rows={3}
          placeholder="e.g. Vendor supplied extra material to round out the batch; accepted as-is."
          value={amendModal.reason}
          onChange={(e) => setAmendModal((prev) => ({ ...prev, reason: e.target.value }))}
        />
      </Form.Item>
    </Modal>
    </>
  );
};

export default DeliveryChallanForm;
